declare global {
  interface HTMLElementTagNameMap {
    "product-audio-player": ProductAudioPlayer;
  }
}

const DEFAULT_BAR_COUNT = 40;
const MIN_BAR_SCALE = 0.18;
const MAX_BAR_SCALE = 1;

class ProductAudioPlayer extends HTMLElement {
  private audio: HTMLAudioElement | null = null;
  private toggle: HTMLButtonElement | null = null;
  private track: HTMLElement | null = null;
  private remainingLabel: HTMLElement | null = null;

  connectedCallback(): void {
    this.audio = this.querySelector<HTMLAudioElement>("[data-audio]");
    this.toggle = this.querySelector<HTMLButtonElement>("[data-toggle]");
    this.track = this.querySelector<HTMLElement>("[data-track]");
    this.remainingLabel = this.querySelector<HTMLElement>("[data-remaining]");

    if (!this.audio || !this.toggle || !this.track) return;

    this.renderBars();
    this.setProgress(0);

    this.toggle.addEventListener("click", this.onToggle);
    this.track.addEventListener("click", this.onSeek);
    this.audio.addEventListener("loadedmetadata", this.onLoadedMetadata);
    this.audio.addEventListener("timeupdate", this.onTimeUpdate);
    this.audio.addEventListener("play", this.onPlayStateChange);
    this.audio.addEventListener("pause", this.onPlayStateChange);
    this.audio.addEventListener("ended", this.onEnded);

    // preload="metadata" may already have resolved before we bound the listener.
    if (this.audio.readyState >= HTMLMediaElement.HAVE_METADATA) {
      this.onLoadedMetadata();
    }
  }

  disconnectedCallback(): void {
    this.toggle?.removeEventListener("click", this.onToggle);
    this.track?.removeEventListener("click", this.onSeek);
    this.audio?.removeEventListener("loadedmetadata", this.onLoadedMetadata);
    this.audio?.removeEventListener("timeupdate", this.onTimeUpdate);
    this.audio?.removeEventListener("play", this.onPlayStateChange);
    this.audio?.removeEventListener("pause", this.onPlayStateChange);
    this.audio?.removeEventListener("ended", this.onEnded);
    this.audio?.pause();
  }

  private onToggle = (): void => {
    if (!this.audio) return;
    if (this.audio.paused) {
      const playAttempt = this.audio.play();
      if (playAttempt && typeof playAttempt.catch === "function") {
        playAttempt.catch(() => {
          // Playback rejected (e.g. unsupported source). Stay in the paused state.
        });
      }
    } else {
      this.audio.pause();
    }
  };

  private onSeek = (event: MouseEvent): void => {
    if (!this.audio || !this.track) return;
    const duration = this.audio.duration;
    if (!Number.isFinite(duration) || duration <= 0) return;

    const bounds = this.track.getBoundingClientRect();
    if (bounds.width <= 0) return;

    const ratio = (event.clientX - bounds.left) / bounds.width;
    const clamped = Math.min(Math.max(ratio, 0), 1);
    this.audio.currentTime = clamped * duration;
    this.setProgress(clamped);
    this.renderTime(this.audio.currentTime, duration);
  };

  private onLoadedMetadata = (): void => {
    if (!this.audio) return;
    // At rest the single timer shows the full duration, then counts down.
    this.renderTime(this.audio.currentTime, this.audio.duration);
  };

  private onTimeUpdate = (): void => {
    if (!this.audio) return;
    const duration = this.audio.duration;
    if (Number.isFinite(duration) && duration > 0) {
      this.setProgress(this.audio.currentTime / duration);
    }
    this.renderTime(this.audio.currentTime, duration);
  };

  private onPlayStateChange = (): void => {
    if (!this.audio || !this.toggle) return;
    const playing = !this.audio.paused;
    this.classList.toggle("is-playing", playing);
    this.toggle.setAttribute("aria-label", playing ? "Pause" : "Play");
  };

  private onEnded = (): void => {
    this.setProgress(0);
    if (this.audio) this.renderTime(0, this.audio.duration);
  };

  private setProgress(ratio: number): void {
    this.style.setProperty("--audio-progress", `${ratio * 100}%`);
  }

  // Single timer: the remaining (count-down) time. Shows the full duration at
  // rest and ticks toward 0:00 as playback runs.
  private renderTime(current: number, duration: number): void {
    if (!this.remainingLabel) return;
    if (!Number.isFinite(duration)) {
      this.remainingLabel.textContent = "--:--";
      return;
    }
    const remaining = Math.max(0, duration - current);
    this.remainingLabel.textContent = formatTime(remaining);
  }

  /**
   * The waveform is decorative — we never decode the audio. Bar heights are
   * derived from the source URL so a given track always renders the same
   * silhouette, while different tracks look distinct.
   */
  private renderBars(): void {
    const base = this.querySelector<HTMLElement>("[data-bars-base]");
    const active = this.querySelector<HTMLElement>("[data-bars-active]");
    if (!base || !active) return;

    const count = Number(this.dataset.bars) || DEFAULT_BAR_COUNT;
    const seed = hashString(this.audio?.getAttribute("src") ?? "");
    const markup = buildBars(count, seed);

    base.innerHTML = markup;
    active.innerHTML = markup;
  }
}

function buildBars(count: number, seed: number): string {
  let state = seed;
  let markup = "";
  for (let i = 0; i < count; i += 1) {
    // xorshift keeps the sequence deterministic without Math.random.
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;
    const noise = Math.abs(state % 1000) / 1000;
    // Taper the ends so the waveform reads as a shape rather than a block.
    const envelope = Math.sin((Math.PI * (i + 0.5)) / count) * 0.4 + 0.6;
    const scale =
      MIN_BAR_SCALE + noise * envelope * (MAX_BAR_SCALE - MIN_BAR_SCALE);
    markup += `<span class="product-audio-player__bar" style="--bar-scale:${scale.toFixed(3)}"></span>`;
  }
  return markup;
}

function hashString(value: string): number {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  // xorshift stalls on a zero seed.
  return hash === 0 ? 1 : hash;
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const total = Math.floor(seconds);
  const minutes = Math.floor(total / 60);
  const remainder = total % 60;
  return `${minutes}:${remainder.toString().padStart(2, "0")}`;
}

if (!customElements.get("product-audio-player")) {
  customElements.define("product-audio-player", ProductAudioPlayer);
}

export { ProductAudioPlayer };
